// ChatThread — the shared chat-message renderer.
//
// One source of truth for the bubble layout used by:
//   - LinkAgentModal (live streaming chat)
//   - ChatHistoryView (read-only past-chat viewer)
//   - the unified chat surface in Phase 1.3+ (when LinkAgentModal
//     retires into the chat page entirely)
//
// Owns rendering only — no streaming state, no composer, no
// approval/ask_user UI. Those live in the consumer that wraps
// this component.

import React, { useState } from 'react';
import { Wrench, ListChecks, CheckCircle2, Loader2, AlertCircle, ChevronDown, ChevronRight, Copy, Check, FileText, Pencil } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { LinkAvatar } from '@/components/icons/LinkAvatar';
import { decodeStrayUnicodeEscapes } from 'jasit-agent-kit';
import { toolResultCard, RICH_CARD_TOOLS, ACTION_TOOLS } from './ToolResultCards';

// The archived views below render the model's RAW tool-call arguments so a
// resumed chat shows what Link actually asked. Raw is the point — but it means
// they skip the repair the engine applies when it CONSUMES a tool call, so a
// model that double-escaped "å" as å shows through verbatim. Every
// user-facing string pulled out of `args` therefore goes through
// decodeStrayUnicodeEscapes first. It is idempotent and only touches non-ASCII
// escapes, so correct text is unaffected.

// truncateOneLine collapses newlines and clips long strings — used
// in the compact archived ask_user / plan_ready summaries so a wall
// of text doesn't dominate the log row.
function truncateOneLine(s: string, max: number): string {
    const flat = s.replace(/\s+/g, ' ').trim();
    if (flat.length <= max) return flat;
    return flat.slice(0, max - 1) + '…';
}

// renderUserText turns `@"source name"` mentions in a user message into inline
// file chips (matching the composer's mention chips), so a sent message reads as
// "… @[stat-card.tsx]" rather than raw `@"stat-card.tsx"`. Plain text (and any
// text with no mentions) passes straight through.
function renderUserText(text: string): React.ReactNode {
    const re = /@"([^"]+)"/g;
    const parts: React.ReactNode[] = [];
    let last = 0;
    let key = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        parts.push(
            <span
                key={`m${key++}`}
                className="inline-flex items-center gap-1 align-middle mx-0.5 px-1.5 py-0.5 rounded-md bg-white/20 text-xs font-medium"
            >
                <FileText className="w-3 h-3 flex-shrink-0" />
                {m[1]}
            </span>,
        );
        last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.length > 0 ? parts : text;
}

// The chat-thread entry shape. Lifted out of LinkAgentModal so both
// callers (modal + history view) construct the same data model and
// rely on one renderer.
// cancelled marks an entry that belonged to a turn the user interrupted
// (Stop button). With the backend's CancelDiscard policy those messages
// are rolled out of Link's memory and never persist, so this flag is an
// in-session affordance only: the entry renders greyed + "Avbruten" so the
// user can see what they cancelled, and it vanishes on the next reload
// (the resumed history is clean). Added to every variant via the shared
// `cancelled?` field below.
type Cancellable = { cancelled?: boolean };

export type ThreadEntry = Cancellable &
    (
        | { kind: 'user'; text: string }
        | { kind: 'assistant'; text: string }
        | {
              kind: 'tool';
              name: string;
              // #288 — the persisted tool_call id (tool_calls[i].id). Carried on
              // hydrated-history entries so the thread seam can reconcile a
              // dangling pending call against the live approval overlay (which
              // knows the same call_id) and drop the duplicate static row.
              callId?: string;
              description?: string;
              args?: unknown;
              resultLabel?: string;
              // #242 — the raw tool result string (forwarded from tool_end).
              // Used to render read-tool results as rich entity cards. Live flow
              // only (populated from the SSE tool_end frame).
              result?: string;
              // response is the tool-result message paired to this call
              // (when the user is the responder — ask_user answer text,
              // plan_ready approval verdict, etc.). Hydrated from
              // persisted messages by pairing tool_calls[i].id with the
              // tool-result message's tool_call_id. Live flow doesn't
              // populate this (the live UI surfaces resolutions
              // separately) — only the saved history uses it.
              response?: string;
              // #111 B1 — whether `response` was a free-form REPLY (the user set
              // the question aside) rather than an answer. A FLAG rather than
              // something the renderer re-derives, because `response` is already
              // cleaned for display by then: the engine's wrapper is what marks a
              // reply, and stripping it for the bubble also strips the evidence.
              // Set at each mapping site, where the raw text is still in hand.
              responseIsReply?: boolean;
          }
        | { kind: 'error'; text: string }
        // #300 — the non-destructive compaction divider. Carries the summary the
        // model produced so the renderer can show it (collapsed) instead of
        // dropping it, which made manually-compacted summaries appear to vanish.
        | { kind: 'compacted'; summary?: string }
    );

// Minimal artefact-shape the renderer needs to type-aware its labels.
// Defined here (not imported from LinkAgentModal) so this shared
// component stays decoupled from the modal's internal ArtifactTab type.
export interface ArtifactRef {
    id: string;
    name: string; // filename, e.g. "annons-foo.md" / "module-bar.md"
}

// Human-readable Swedish labels for Link's tool calls. Falls back
// to the raw tool name when an entry isn't listed here (e.g. an MCP
// or app-specific tool not yet given a friendly name).
//
// Artefakt-verb (emit/update/get/delete_artifact) använder den
// GENERISKA fallback-etiketten här ("utkast"). När den anropande
// `<ThreadEntryView artifacts={...}>` får artefakt-listan kan vi
// dock typeawarea dem till "annons-utkast" / "modul-utkast" via
// labelForArtifactTool below — så användaren ser vilken sorts
// utkast Link arbetar på, inte bara "utkast".
const TOOL_LABELS: Record<string, string> = {
    list_visible_sources: 'Tittar på dina källor',
    read_source_content: 'Läser källans innehåll',
    list_source_outline: 'Hämtar dokumentstruktur',
    read_source_section: 'Läser sektion',
    ask_user: 'Frågar dig något',
    fetch_url: 'Hämtar info från en URL',
    current_time: 'Kollar aktuell tid',
    plan_ready: 'Lägger fram en plan',
    // Generic fallback — labelForArtifactTool produces the type-aware
    // variant when an artefact-shape is in reach (filename prefix
    // tells us annons vs modul). list_artifacts / delete_artifacts
    // operate on the WHOLE set so they stay generic.
    emit_artifact: 'Skapar utkast',
    update_artifact: 'Uppdaterar utkast',
    get_artifact: 'Läser utkast',
    delete_artifact: 'Tar bort utkast',
    delete_artifacts: 'Tar bort flera utkast',
    list_artifacts: 'Listar utkast',
    todo: 'Uppdaterar checklista',
    save_memory: 'Sparar minne',
    recall_memory: 'Hämtar minne',
    get_organisation_info: 'Hämtar organisationsinfo',
    get_unit_info: 'Hämtar enhetsinfo',
    get_user_profile: 'Hämtar din profil',
};

// Completed-action phrasing for the ACTION_TOOLS receipt ("Link <label> — …").
// Past tense + lowercase initial so it reads as a finished event in a sentence
// ("Link bjöd in medarbetare"). Falls back to TOOL_LABELS / the raw name.
const ACTION_LABELS: Record<string, string> = {
    invite_employees: 'bjöd in medarbetare',
    decide_application: 'beslutade om en ansökan',
    assign_onboarding: 'tilldelade ett onboarding-program',
    set_posting_status: 'ändrade annonsens status',
    attach_courses_to_posting: 'kopplade moduler till annonsen',
    emit_artifact: 'skapade ett utkast',
    update_artifact: 'uppdaterade ett utkast',
    delete_artifact: 'tog bort ett utkast',
    delete_artifacts: 'tog bort flera utkast',
    save_skill: 'sparade en färdighet',
    save_memory: 'sparade ett minne',
};

// Returns 'annons' / 'modul' for the recognised artefact types, or
// null when the filename doesn't match either known prefix. Adding a
// third artefact type means one more case here + one verb-table
// extension below.
function artifactKindFromName(filename: string | undefined): 'annons' | 'modul' | null {
    if (!filename) return null;
    if (filename.startsWith('annons-')) return 'annons';
    if (filename.startsWith('module-')) return 'modul';
    return null;
}

// Per-artefact-type label suffixes. Pattern is "<verb> <type>-utkast"
// so the language reads naturally (Swedish compound) — "Skapar
// modul-utkast", "Uppdaterar annons-utkast", etc. delete_artifacts
// stays generic because the batch op can mix types.
const ARTIFACT_VERBS: Record<string, string> = {
    emit_artifact: 'Skapar',
    update_artifact: 'Uppdaterar',
    get_artifact: 'Läser',
    delete_artifact: 'Tar bort',
};

// Compute the type-aware label for an artefact tool call when we
// can resolve the artefact's kind; otherwise return null and let the
// caller fall back to the generic TOOL_LABELS entry. The lookup
// order is:
//   1. emit_artifact: args.name (the filename Link picked) — always
//      available on the call itself, no artefacts list needed.
//   2. update / get / delete: args.id, looked up against the
//      artefacts list to recover the filename → kind.
function labelForArtifactTool(
    toolName: string,
    args: unknown,
    artifacts: ArtifactRef[] | undefined,
): string | null {
    const verb = ARTIFACT_VERBS[toolName];
    if (!verb) return null;

    let kind: 'annons' | 'modul' | null = null;
    const a = (args ?? {}) as Record<string, unknown>;

    if (toolName === 'emit_artifact') {
        kind = artifactKindFromName(typeof a.name === 'string' ? a.name : undefined);
    } else {
        const id = typeof a.id === 'string' ? a.id : undefined;
        if (id && artifacts) {
            kind = artifactKindFromName(artifacts.find((x) => x.id === id)?.name);
        }
    }
    if (!kind) return null;
    return `${verb} ${kind}-utkast`;
}

interface ChatThreadProps {
    entries: ThreadEntry[];
    // When true, draw a slightly softer scheme for the "archived /
    // read-only" feel (history view). Defaults to live look.
    readOnly?: boolean;
    // Optional. When provided, artefact-shape tool labels
    // (emit/update/get/delete_artifact) become type-aware:
    // "Skapar annons-utkast" / "Uppdaterar modul-utkast" instead of
    // the generic "Skapar utkast". The live modal passes its
    // ArtifactTab[] (downcasted to ArtifactRef); the history view
    // can pass its restored LiveArtifact list. Omitting it falls
    // back to generic labels — safe default.
    artifacts?: ArtifactRef[];
}

const ChatThread: React.FC<ChatThreadProps> = ({ entries, readOnly, artifacts }) => {
    if (entries.length === 0) {
        // Empty state — caller (modal welcome card, history empty state)
        // typically handles this themselves; we render nothing.
        return null;
    }
    return (
        <div className={'space-y-3 ' + (readOnly ? 'opacity-95' : '')}>
            {entries.map((entry, i) => (
                <ThreadEntryView key={i} entry={entry} artifacts={artifacts} />
            ))}
        </div>
    );
};

// #52 — a small hover-revealed Copy affordance for any TEXT bubble (assistant
// reply, user message, or the natural-language parts of a tool exchange like an
// ask_user question/answer). Manages its own "Kopierat" flash. The reveal-on-
// hover only triggers when an ancestor carries the `group/msg` class; the
// enclosing bubble's flex column controls left/right alignment. No-ops on empty
// text. Copy stays on genuine text — never on rendered tool WIDGETS (checklists,
// profile cards, option chips), where there's no meaningful string to copy.
const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label = 'Kopiera' }) => {
    const [copied, setCopied] = useState(false);
    if (!text.trim()) return null;
    const doCopy = () => {
        void navigator.clipboard?.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    };
    return (
        <button
            type="button"
            onClick={doCopy}
            title={copied ? 'Kopierat' : label}
            className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] text-gray-400 rounded opacity-0 group-hover/msg:opacity-100 focus:opacity-100 hover:text-gray-700 hover:bg-gray-100 transition-all"
        >
            {copied ? (
                <><Check className="w-3 h-3 text-emerald-600" /> Kopierat</>
            ) : (
                <><Copy className="w-3 h-3" /> {label}</>
            )}
        </button>
    );
};

// Single-entry renderer — exported so callers that want full
// control over the layout (e.g. injecting an approval UI inline
// between two thread entries) can compose with the same shape.
export const ThreadEntryView: React.FC<{
    entry: ThreadEntry;
    artifacts?: ArtifactRef[];
    streaming?: boolean;
    // Render a user message's text — the modal passes the kit's <MentionText>
    // (resolves @mentions against the live source library → pills). Defaults to
    // the bespoke quote-only chip renderer for callers without a source list
    // (e.g. the read-only history view).
    renderText?: (text: string) => React.ReactNode;
    // #258 — live chat passes a composer-insert callback so capability-card rows
    // become clickable (inject a starter prompt, don't auto-send). Omitted in the
    // read-only history view, where the rows render non-interactive.
    onCapabilityPick?: (text: string) => void;
    // #52 — the string a user-message Copy button writes to the clipboard. The
    // modal passes stripInjectedContext so Copy yields the clean typed text (not
    // the persisted model-shaped bubble with its <<<ctx>>> block). Defaults to
    // identity — matches each caller's own bubble display.
    copyUserText?: (text: string) => string;
}> = ({ entry, artifacts, streaming, renderText, onCapabilityPick, copyUserText }) => {
    const node = ((): React.ReactNode => {
    if (entry.kind === 'user') {
        // #52 — copy the clean typed text (stripped of any injected <<<ctx>>>
        // block), mirroring the assistant Copy: hover-revealed, right-aligned
        // under the bubble.
        const plain = (copyUserText ?? ((t) => t))(entry.text);
        return (
            <div className="flex flex-col items-end group/msg">
                <div className="max-w-[80%] px-3 py-2 bg-[#1A4D4F] text-white rounded-xl text-sm whitespace-pre-wrap">
                    {(renderText ?? renderUserText)(entry.text)}
                </div>
                <CopyButton text={plain} />
            </div>
        );
    }
    if (entry.kind === 'assistant') {
        if (!entry.text) return null;
        return (
            <div className="flex justify-start gap-2 group/msg">
                <LinkAvatar className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <div className="max-w-[80%] min-w-0">
                    <div className="px-3 py-2 bg-gray-100 text-gray-900 rounded-xl text-sm">
                        {/* Phase 5 E2 — streaming caret. While this assistant
                            message is the one currently streaming, an inline
                            blinking block caret (▍) is appended after the last
                            rendered block via an after: pseudo-element so it
                            sits right at the end of the text, not on its own
                            line. */}
                        <div
                            className={`prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 ${
                                streaming
                                    ? "[&>*:last-child]:after:content-['▍'] [&>*:last-child]:after:ml-0.5 [&>*:last-child]:after:animate-pulse [&>*:last-child]:after:text-[#1A4D4F] [&>*:last-child]:after:font-normal"
                                    : ''
                            }`}
                        >
                            <ReactMarkdown>{entry.text}</ReactMarkdown>
                        </div>
                    </div>
                    {/* Phase 5 D3 — copy the raw markdown. Hover-revealed,
                        hidden while streaming (copy a finished reply). */}
                    {!streaming && <CopyButton text={entry.text} label="Kopiera svar" />}
                </div>
            </div>
        );
    }
    if (entry.kind === 'tool') {
        // Special-case `todo`: render the actual checklist so the user can
        // see what Link thinks the plan is, not just "Link använder: todo".
        if (entry.name === 'todo') {
            return <TodoToolView args={entry.args} />;
        }
        // Special-case `ask_user`: surface the actual question + options
        // (read-only) so a resumed chat shows what Link asked, not just
        // "Frågar dig något". Live answer-flow lives elsewhere (modal's
        // PendingAskUserView); this is for browsing/resume.
        if (entry.name === 'ask_user') {
            return <AskUserArchivedView args={entry.args} response={entry.response} isReply={entry.responseIsReply} />;
        }
        // Special-case `plan_ready`: green card same as the live
        // PendingApprovalView, showing the plan steps. Footer flips
        // based on the user's decision (approved / amended / denied
        // / unanswered) recovered from the tool-response message.
        if (entry.name === 'plan_ready') {
            return <PlanReadyArchivedView args={entry.args} response={entry.response} />;
        }
        // Special-case `get_user_profile`: a gated approval tool. When it actually
        // ran (approved) the result JSON is present → the rich ProfileCard below
        // renders it (with its own "Godkänd" badge). Only when it was denied / left
        // unanswered (no result) do we fall back to the compact verdict line, so a
        // gated-but-declined call still reads as "nothing was registered" rather
        // than the bare label. Works live and on reopen (both carry a result once run).
        if (entry.name === 'get_user_profile') {
            const profileCard = toolResultCard(entry.name, entry.result, onCapabilityPick);
            if (profileCard) return profileCard;
            return (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Wrench className="w-3 h-3" />
                    <span>
                        Link: <span className="font-medium text-gray-700">{TOOL_LABELS.get_user_profile}</span>
                        <span className="text-gray-400 font-mono"> (get_user_profile)</span>
                        <span className="italic text-gray-400"> → Inget svar registrerat.</span>
                    </span>
                </div>
            );
        }
        // #242 — read tools (list_employees / list_applicants / get_org_stats /
        // …) render their result as a rich entity card. Falls through to the
        // generic tool-log row while the result is still streaming in (or if it
        // can't be parsed).
        const card = toolResultCard(entry.name, entry.result, onCapabilityPick);
        if (card) return card;
        // ACTION tools — Link DID something with real-world effect (invited
        // people, decided an application, published a posting, edited a draft).
        // Render a confirmed-event RECEIPT (teal check), not a greyed debug row,
        // so the record survives the "Verktygsanrop" toggle and reads as an event.
        if (ACTION_TOOLS.has(entry.name)) {
            const doneLabel =
                ACTION_LABELS[entry.name] ??
                labelForArtifactTool(entry.name, entry.args, artifacts) ??
                TOOL_LABELS[entry.name] ??
                entry.name;
            return (
                <div className="flex items-start gap-2 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#1A4D4F]" />
                    <div className="min-w-0">
                        <span className="text-gray-700">
                            Link <span className="font-medium">{doneLabel}</span>
                        </span>
                        {entry.resultLabel && <span className="text-gray-500"> — {entry.resultLabel}</span>}
                    </div>
                </div>
            );
        }
        // Type-aware label for artefact-shape tools when we can
        // resolve the kind (emit_artifact reads args.name; the rest
        // look up args.id in artifacts). Falls back to the generic
        // entry in TOOL_LABELS when artefacts list is absent or the
        // id doesn't match (e.g. update on a since-deleted artefact).
        const friendly =
            labelForArtifactTool(entry.name, entry.args, artifacts) ??
            TOOL_LABELS[entry.name] ??
            entry.name;
        return (
            <div className="flex items-center gap-2 text-xs text-gray-500">
                <Wrench className="w-3 h-3" />
                <span>
                    Link: <span className="font-medium text-gray-700">{friendly}</span>
                    {friendly !== entry.name && (
                        <span className="text-gray-400 font-mono"> ({entry.name})</span>
                    )}
                    {entry.description && <span className="text-gray-400"> — {entry.description}</span>}
                    {entry.resultLabel && (
                        <span className="text-gray-400"> → {entry.resultLabel}</span>
                    )}
                </span>
            </div>
        );
    }
    if (entry.kind === 'error') {
        return (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{entry.text}</span>
            </div>
        );
    }
    return null;
    })();

    if (!node) return null;
    // Phase 5 D1 — a cancelled (interrupted) entry renders dimmed with an
    // "Avbruten" tag. The backend discarded it, so this is in-session-only
    // feedback (gone on the next reload). The tag aligns to the entry's
    // own side: right for the user's bubble, left for Link/tool lines.
    if (!entry.cancelled) return node;
    const align = entry.kind === 'user' ? 'justify-end' : 'justify-start';
    return (
        <div className="opacity-50">
            {node}
            <div className={`flex ${align} mt-0.5`}>
                <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                    Avbruten
                </span>
            </div>
        </div>
    );
};

// AskUserArchivedView — renders a resolved ask_user as CONVERSATION, not a tool
// log (it IS a conversational turn: Link asked, you answered). Hybrid layout:
//   • the question as an assistant bubble (grey, left — like Link's other messages)
//   • the answer as a green USER bubble (right), with a distinct "Eget svar" badge
//     when it was a typed custom answer rather than a picked option
//   • the offered alternatives as a SUBTLE collapsed expander below the answer, so
//     the decision context (what else was on the table) is on-demand, not clutter
// Data is all present already: args.options is the offered set, `response` is the
// cleaned answer (see cleanAskUserResponse) — the model keeps the full options list
// in the tool-call args regardless.
// reconstructAskAnswer recovers which options the user picked (and any free-typed
// custom parts) from a STORED ask_user answer. The answer is persisted as a comma+
// space joined string (`answers.join(', ')` in useLinkAgentSession), which is
// AMBIGUOUS whenever an option's own text contains ", " — e.g.
// "Grundläggande (IT, policies, processer)" or "Säkerhet, juridik och compliance".
// Naively splitting on /,\s+/ shreds such options into fragments that match no
// option, which caused a false "Eget svar" badge, missing checkmarks in the
// alternatives expander, and a wrong "N andra alternativ" count (#33).
//
//   • single-choice (multi false): the whole answer is exactly ONE part — an exact
//     option match → picked, otherwise a custom typed answer. No delimiter parsing,
//     so a custom answer (or a picked option) containing commas stays intact.
//   • multi-choice: greedily consume the answer left-to-right, matching known
//     options as prefixes (longest option first) so "A, B" (one option) wins over
//     "A" + "B". A segment matching no option is a best-effort custom part.
function reconstructAskAnswer(
    resp: string,
    options: string[],
    multi: boolean,
): { picked: string[]; custom: string[] } {
    const trimmed = resp.trim();
    if (!trimmed) return { picked: [], custom: [] };

    if (!multi) {
        return options.includes(trimmed)
            ? { picked: [trimmed], custom: [] }
            : { picked: [], custom: [trimmed] };
    }

    const byLen = options.filter((o) => o.length > 0).sort((a, b) => b.length - a.length);
    const sep = /^,\s+/;
    const picked: string[] = [];
    const custom: string[] = [];
    let rest = trimmed;
    while (rest.length > 0) {
        const opt = byLen.find(
            (o) => rest.startsWith(o) && (rest.length === o.length || sep.test(rest.slice(o.length))),
        );
        if (opt) {
            picked.push(opt);
            rest = rest.slice(opt.length).replace(sep, '');
            continue;
        }
        const m = /^(.*?),\s+/.exec(rest);
        if (m) {
            const seg = m[1].trim();
            if (seg) custom.push(seg);
            rest = rest.slice(m[0].length);
        } else {
            custom.push(rest.trim());
            rest = '';
        }
    }
    return { picked, custom };
}

const AskUserArchivedView: React.FC<{ args: unknown; response?: string; isReply?: boolean }> = ({ args, response, isReply = false }) => {
    // Expanded by default — the offered alternatives (and which were picked) are
    // useful context to see at a glance, not something to hunt for behind a click.
    const [showAlternatives, setShowAlternatives] = useState(true);
    let parsed: any = args;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            parsed = null;
        }
    }
    const question: string = decodeStrayUnicodeEscapes((parsed?.question || parsed?.prompt || '').trim());
    // Options are repaired too, not just for display: the persisted ANSWER was
    // recorded from the engine's already-repaired copy, so leaving these raw
    // would make reconstructAskAnswer fail to match a picked option below.
    const options: string[] = Array.isArray(parsed?.options)
        ? parsed.options
              .filter((o: unknown): o is string => typeof o === 'string')
              .map((o: string) => decodeStrayUnicodeEscapes(o))
        : [];
    const multi = parsed?.multi === true;
    const respText = (response || '').trim();
    const isHeal = respText.startsWith('(Konversationen återupptogs');
    const answered = !!respText && !isHeal;

    // Classify the answer against the offered options WITHOUT naively splitting on
    // commas (options may contain their own — see reconstructAskAnswer / #33).
    // A REPLY is not an answer: it must not be classified against the offered
    // options, or it gets badged "Eget svar" as though the user deliberately
    // chose it (#111 B1). Supplied by the caller — see responseIsReply.
    const { picked: pickedList, custom: customParts } = answered && !isReply
        ? reconstructAskAnswer(respText, options, multi)
        : { picked: [], custom: [] };
    const picked = new Set(pickedList);
    const hasCustom = customParts.length > 0;
    // Alternatives worth surfacing = options the user did NOT pick.
    const unchosen = options.filter((o) => !picked.has(o));

    return (
        <div className="space-y-1.5">
            {/* Question — assistant bubble (copyable text, like any reply) */}
            {question && (
                <div className="flex justify-start gap-2 group/msg">
                    <LinkAvatar className="w-6 h-6 flex-shrink-0 mt-0.5" />
                    <div className="max-w-[80%] min-w-0">
                        <div className="px-3 py-2 bg-gray-100 text-gray-900 rounded-xl text-sm whitespace-pre-wrap">
                            {question}
                        </div>
                        <CopyButton text={question} label="Kopiera fråga" />
                    </div>
                </div>
            )}

            {/* Answer — green user bubble (or a muted "no answer" note) */}
            {answered ? (
                <div className="flex flex-col items-end gap-1">
                    <div className="max-w-[80%] flex flex-col items-end gap-1 group/msg">
                        {isReply && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-[#1A4D4F]/70">
                                <Pencil className="w-3 h-3" /> Svarade inte — skrev i stället
                            </span>
                        )}
                        {hasCustom && !isReply && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-[#1A4D4F]/70">
                                <Pencil className="w-3 h-3" /> Eget svar
                            </span>
                        )}
                        <div
                            className={`px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${
                                hasCustom
                                    ? 'bg-[#1A4D4F]/[0.08] text-[#1A4D4F] border border-dashed border-[#1A4D4F]/40'
                                    : 'bg-[#1A4D4F] text-white'
                            }`}
                        >
                            {respText}
                        </div>
                        <CopyButton text={respText} label="Kopiera svar" />
                        {/* Alternatives expander — subtle, collapsed by default */}
                        {unchosen.length > 0 && (
                            <div className="flex flex-col items-end">
                                <button
                                    type="button"
                                    onClick={() => setShowAlternatives((v) => !v)}
                                    className="inline-flex items-center gap-0.5 text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
                                    title={showAlternatives ? 'Dölj alternativ' : 'Visa alternativ'}
                                >
                                    {showAlternatives ? (
                                        <ChevronDown className="w-3 h-3" />
                                    ) : (
                                        <ChevronRight className="w-3 h-3" />
                                    )}
                                    <span>
                                        {unchosen.length} {unchosen.length === 1 ? 'annat alternativ' : 'andra alternativ'}
                                    </span>
                                </button>
                                {showAlternatives && (
                                    <div className="mt-1 flex flex-wrap justify-end gap-1">
                                        {options.map((o, i) => (
                                            <span
                                                key={i}
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
                                                    picked.has(o)
                                                        ? 'border-[#1A4D4F]/30 text-[#1A4D4F] bg-[#1A4D4F]/[0.06]'
                                                        : 'border-gray-200 text-gray-400 bg-gray-50'
                                                }`}
                                            >
                                                {picked.has(o) && <Check className="w-3 h-3" />}
                                                {truncateOneLine(o, 60)}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex justify-end">
                    <span className="text-[11px] italic text-gray-400">Inget svar registrerat.</span>
                </div>
            )}
        </div>
    );
};

// PlanReadyArchivedView — a resolved plan_ready rendered AS conversation: the plan
// as an assistant card (grey, left, expandable steps) and the user's decision as a
// right-aligned verdict. plan_ready is a real exchange (Link proposed, you decided),
// so it renders identically whether tool-calls are toggled on or off. Verdict:
//   approved  — you accepted the plan
//   changes   — you denied WITH feedback (asked to change something) → show it
//   denied    — you declined outright (a bare cancel, no feedback)
//   unanswered— left before deciding
type PlanReadyVerdict = 'approved' | 'changes' | 'denied' | 'unanswered';

const PlanReadyArchivedView: React.FC<{ args: unknown; response?: string }> = ({ args, response }) => {
    // Expanded by default — the plan steps are the substance of the exchange, so
    // show them up front rather than collapsed behind the "N steg" toggle.
    const [expanded, setExpanded] = useState(true);
    let parsed: any = args;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            parsed = null;
        }
    }
    const steps: string[] = Array.isArray(parsed?.steps)
        ? parsed.steps
              .filter((s: unknown): s is string => typeof s === 'string')
              .map((s: string) => decodeStrayUnicodeEscapes(s))
        : [];
    const stepCount = steps.length;
    // #52 — the plan as plain text for Copy: a numbered list, independent of the
    // expand/collapse UI state.
    const planText = steps.map((s, i) => `${i + 1}. ${s}`).join('\n');

    const respText = (response || '').trim();
    const isHeal = respText.startsWith('(Konversationen återupptogs');
    let verdict: PlanReadyVerdict = 'approved';
    let feedback = '';
    if (!respText || isHeal) {
        verdict = 'unanswered';
    } else if (
        /^denied\b/i.test(respText) ||
        /^(amend|föreslå ändringar|avbryt)/i.test(respText) ||
        respText.toLowerCase().includes('denied:')
    ) {
        // A denial carries optional feedback ("denied: <what to change>"). Feedback
        // present → the user asked for CHANGES; a bare cancel → an outright decline.
        const m = /denied:\s*(.*)$/is.exec(respText);
        feedback = (m ? m[1] : '').trim();
        const bareCancel = !feedback || /^(avbruten|avbröt|avbryt|cancel)/i.test(feedback);
        verdict = bareCancel ? 'denied' : 'changes';
    }

    return (
        <div className="space-y-1.5">
            {/* Plan — assistant card (copyable as a numbered list) */}
            <div className="flex justify-start gap-2 group/msg">
                <LinkAvatar className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <div className="max-w-[80%] min-w-0">
                    <div className="px-3 py-2 bg-gray-100 text-gray-900 rounded-xl text-sm">
                        <div className="flex items-center gap-1.5 font-medium text-gray-700">
                            <ListChecks className="w-3.5 h-3.5 text-[#1A4D4F]" />
                            Link la fram en plan
                            {stepCount > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setExpanded((v) => !v)}
                                    className="ml-1 inline-flex items-center gap-0.5 text-xs font-normal text-gray-400 hover:text-gray-600 transition-colors"
                                    title={expanded ? 'Dölj plansteg' : 'Visa plansteg'}
                                >
                                    {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                    <span>{stepCount} {stepCount === 1 ? 'steg' : 'steg'}</span>
                                </button>
                            )}
                        </div>
                        {expanded && stepCount > 0 && (
                            <ol className="mt-1.5 space-y-1 list-decimal list-inside marker:text-gray-300">
                                {steps.map((s, i) => (
                                    <li key={i} className="text-gray-600 leading-relaxed">
                                        {s}
                                    </li>
                                ))}
                            </ol>
                        )}
                    </div>
                    {planText && <CopyButton text={planText} label="Kopiera plan" />}
                </div>
            </div>

            {/* Verdict — right-aligned decision */}
            <div className="flex justify-end">
                {verdict === 'approved' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-[#1A4D4F] text-white">
                        <Check className="w-3.5 h-3.5" /> Du godkände planen
                    </span>
                )}
                {verdict === 'changes' && (
                    <div className="max-w-[80%] flex flex-col items-end gap-1">
                        <span className="text-[11px] text-gray-400">Du bad om ändringar</span>
                        <div className="px-3 py-2 rounded-xl text-sm whitespace-pre-wrap bg-[#1A4D4F]/[0.08] text-[#1A4D4F] border border-dashed border-[#1A4D4F]/40">
                            {feedback}
                        </div>
                    </div>
                )}
                {verdict === 'denied' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-gray-100 text-gray-500 border border-gray-200">
                        Du avböjde planen
                    </span>
                )}
                {verdict === 'unanswered' && (
                    <span className="text-[11px] italic text-gray-400">Inget svar registrerat.</span>
                )}
            </div>
        </div>
    );
};

const TodoToolView: React.FC<{ args: unknown }> = ({ args }) => {
    let parsed: any = args;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            parsed = null;
        }
    }
    const todos: Array<{ content: string; status: string }> = Array.isArray(parsed?.todos)
        ? parsed.todos.map((t: { content: string; status: string }) => ({
              ...t,
              content: decodeStrayUnicodeEscapes(t?.content),
          }))
        : [];

    if (todos.length === 0) {
        return (
            <div className="flex items-center gap-2 text-xs text-gray-500">
                <ListChecks className="w-3 h-3" />
                <span>
                    Link: <span className="font-medium text-gray-700">Uppdaterar checklista</span>
                    <span className="text-gray-400 font-mono"> (todo)</span>
                </span>
            </div>
        );
    }

    const statusIcon = (s: string) => {
        if (s === 'completed') return <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />;
        if (s === 'in_progress') return <Loader2 className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />;
        return (
            <span className="w-3.5 h-3.5 inline-block border-2 border-gray-300 rounded-full flex-shrink-0" />
        );
    };

    return (
        <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
            <div className="flex items-center gap-2 text-xs text-gray-600">
                <ListChecks className="w-3.5 h-3.5 text-[#1A4D4F]" />
                <span className="font-medium">Checklista</span>
                <span className="text-gray-400 font-mono">(todo)</span>
            </div>
            <ul className="space-y-1 pl-1">
                {todos.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs">
                        {statusIcon(t.status)}
                        <span
                            className={`leading-relaxed ${
                                t.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'
                            }`}
                        >
                            {t.content}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

// threadEntriesFromPersistedMessages converts the framework's
// ai.Message[] (as we store it in employer_chats.messages) into
// the renderer-friendly ThreadEntry[]. Used by both the resume
// path (LinkAgentModal hydration) and the read-only history view.
//
// Strategy:
//   - system messages: skipped (not user-facing)
//   - user content: → kind:'user'
//   - assistant content: → kind:'assistant'
//   - assistant tool_calls: → one kind:'tool' per call
//   - tool result messages: skipped (the model has them in
//     conversation memory; rendering them adds noise without
//     value for someone reading the past chat)
export interface PersistedMessage {
    role: 'system' | 'user' | 'assistant' | 'tool';
    content?: string | null;
    name?: string;
    tool_call_id?: string;
    tool_calls?: unknown[];
}

// cleanAskUserResponse strips the framework's wrapper prose from an
// ask_user tool result so the persisted response looks the same as
// the live-attached one. The framework's askUser tool wraps the
// raw answer with English instructional text for the LLM (e.g.
// `The user selected: X` or `The user did not pick any offered
// option — they typed their own answer: "X". Honor it as ...`),
// which is great for the model but looks strange in the user-
// facing Swedish "Du svarade: …" line.
//
// Pass-throughs: empty, the heal sentinel ("(Konversationen
// återupptogs…"), and any unrecognised format — AskUserArchivedView
// already special-cases empty and heal.
function cleanAskUserResponse(raw: string): string {
    const s = raw.trim();
    if (!s) return s;
    if (s.startsWith('(Konversationen återupptogs')) return s;
    if (s === 'The user dismissed the question without choosing an option.') return '';

    // Both options + custom typed: "The user selected: A. They also
    // typed their own answer: "X". Treat the typed answer as a
    // deliberate choice, not a mistake."
    let m = /^The user selected: (.+?)\. They also typed their own answer: (.+?)\. Treat/.exec(s);
    if (m) {
        return `${m[1]}, ${unquote(m[2])}`;
    }
    // Custom typed only: "The user did not pick any offered option —
    // they typed their own answer: "X". Honor it as their deliberate
    // choice and proceed with it."
    m = /^The user did not pick any offered option — they typed their own answer: (.+?)\. Honor/.exec(s);
    if (m) {
        return unquote(m[1]);
    }
    // Selected option(s) only: "The user selected: A" (or "A, B, C"
    // for multi-select). No trailing period or follow-on prose.
    m = /^The user selected: (.+)$/.exec(s);
    if (m) {
        return m[1];
    }
    // #111 B1 — the user did NOT answer; they replied instead. Without this the
    // whole English instruction would land in the user-facing Swedish bubble,
    // and worse, be labelled "Eget svar" — which is precisely the conflation
    // this path exists to avoid: a redirect is not a deliberate answer.
    m = askReplyPattern.exec(s);
    if (m) {
        return unquote(m[1]);
    }
    return s;
}

// The engine's wrapper for RespondAskWithText (chat/ask_user.go,
// askUserResultText). The pattern, the builder and the cleaner all live
// together so they can never drift apart.
const askReplyPattern =
    /^The user did not answer the question — they replied instead: (.+?)\. Take their reply/;

/**
 * Build the exact response text the ENGINE will persist for a free-form reply.
 *
 * The live thread has no server response yet — it shows an optimistic verdict —
 * so without this the live view would classify the reply as an answer and badge
 * it "Eget svar", then silently change to "Svarade inte" after a reload. Making
 * the optimistic value byte-identical to the persisted one means the two views
 * agree by construction rather than by two parallel code paths agreeing to.
 */
export function buildAskReplyResponse(text: string): string {
    return `The user did not answer the question — they replied instead: ${JSON.stringify(text)}. ` +
        'Take their reply on its own terms and continue; do not re-ask the question ' +
        'unless their reply asks you to.';
}

/**
 * Clean an ask_user response for display, leaving every other tool untouched.
 * Exported so the LIVE mapping can apply exactly the same transform the history
 * mapping does — the two rendering identically is the property that matters.
 */
export function cleanAskUserResponseForDisplay(raw: string | undefined, toolName: string): string | undefined {
    if (raw == null || toolName !== 'ask_user') return raw;
    return cleanAskUserResponse(raw);
}

/** True when this response is a free-form REPLY rather than an answer. */
export function isAskUserReply(raw: string | undefined): boolean {
    return !!raw && askReplyPattern.test(raw.trim());
}

// Strip a single pair of wrapping double-quotes (the framework
// pre-quotes custom answers via %q before joining).
function stripWrappingQuotes(s: string): string {
    const m = /^"(.*)"$/s.exec(s);
    return m ? m[1] : s;
}

// Unquote a Go %q-style string. Stripping the outer quotes is not enough: %q
// also ESCAPES inner ones, so `Vi vill ha "senior" nivå` comes back as
// `Vi vill ha \"senior\" nivå` and the backslashes reach the user's bubble.
// Go %q and JSON produce the same escaping for the cases that occur here, so
// JSON.parse does the real unescaping; anything it can't read (e.g. Go's \xNN)
// falls back to the previous strip-only behaviour rather than throwing.
function unquote(s: string): string {
    const t = s.trim();
    if (t.startsWith('"') && t.endsWith('"')) {
        try {
            const parsed = JSON.parse(t);
            if (typeof parsed === 'string') return parsed;
        } catch { /* not JSON-compatible — fall through */ }
    }
    return stripWrappingQuotes(t);
}

// Extracts a plain-text string from a persisted message's `content`
// field regardless of shape. Framework messages can be:
//   - string (the common case — typed text messages)
//   - null/undefined (assistant messages with only tool_calls)
//   - multimodal array (OpenAI-style content parts when a turn
//     attached images/documents: `[{type:"text",text:"..."},
//     {type:"image_url",image_url:{...}}]`). Image parts carry no
//     text — we drop them at the renderer layer (the live thread
//     handles attachments separately).
// The original `(m.content || '').trim()` crashed when content was
// an array (truthy non-string → .trim is undefined). This helper
// guards every read.
function persistedContentText(content: unknown): string {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
        return content
            .filter((p): p is { type?: string; text?: string } => !!p && typeof p === 'object')
            .map((p) => (p.type === 'text' && typeof p.text === 'string' ? p.text : ''))
            .join('');
    }
    return '';
}

/**
 * Where a non-destructive compaction folded this chat, for rendering the same
 * divider a live compaction streams.
 *
 * `through` indexes the FULL transcript, while `msgs` may be a window onto its
 * tail, so `offset` (total − msgs.length) translates between them. Passing the
 * raw index without the offset would place the divider hundreds of messages off
 * — silently, and always in the wrong direction.
 */
export interface PersistedCompaction {
    through: number;
    summary?: string;
    offset: number;
}

export function threadEntriesFromPersistedMessages(
    msgs: PersistedMessage[],
    compaction?: PersistedCompaction,
): ThreadEntry[] {
    // First pass: collect tool-result content keyed by tool_call_id so
    // we can pair each ask_user / plan_ready / etc. call with the user's
    // resolution when rendering. Without this, resumed/historical chats
    // show the question but not the answer (or the plan but not the
    // approval), which is exactly the gap the user flagged.
    const responses: Record<string, string> = {};
    for (const m of msgs) {
        if (m.role === 'tool' && m.tool_call_id) {
            responses[m.tool_call_id] = persistedContentText(m.content);
        }
    }

    const out: ThreadEntry[] = [];
    // The fold point, expressed in THIS slice's coordinates. Emitted during the
    // walk rather than spliced in afterwards, because the mapping is not 1:1 —
    // system and tool messages vanish, and one assistant message can produce a
    // text entry plus several tool entries. An index computed against `msgs`
    // means nothing once the array becomes `out`.
    //
    // Negative means the fold is older than the loaded window: the divider
    // belongs above everything on screen, so it is NOT drawn here. Loading
    // older history brings it into range and it appears in its real place —
    // better than pinning it to the top, which would claim the fold happened
    // somewhere it did not.
    const foldAt =
        compaction && compaction.summary && compaction.through > 0
            ? compaction.through - compaction.offset
            : -1;
    let foldDrawn = foldAt < 0;

    for (let i = 0; i < msgs.length; i++) {
        const m = msgs[i];
        if (!foldDrawn && i >= foldAt) {
            out.push({ kind: 'compacted', summary: compaction!.summary });
            foldDrawn = true;
        }
        if (m.role === 'system' || m.role === 'tool') continue;
        const text = persistedContentText(m.content).trim();
        if (m.role === 'user' && text) {
            out.push({ kind: 'user', text });
            continue;
        }
        if (m.role === 'assistant') {
            if (text) out.push({ kind: 'assistant', text });
            const tcs = (m.tool_calls || []) as Array<{
                id?: string;
                function?: { name?: string; arguments?: string };
            }>;
            for (const tc of tcs) {
                const name = tc?.function?.name;
                if (!name) continue;
                // Preserve raw args string so renderers can parse + show
                // tool-specific content (e.g. ask_user question + options
                // as a read-only bubble instead of a generic "Frågar dig
                // något" log line).
                let args: unknown = tc.function?.arguments;
                if (typeof args === 'string') {
                    try {
                        args = JSON.parse(args);
                    } catch {
                        // Leave as raw string — renderer falls back to
                        // generic tool entry.
                    }
                }
                let response = tc.id ? responses[tc.id] : undefined;
                // Detect BEFORE cleaning — cleaning removes the very wrapper
                // that distinguishes a reply from an answer.
                const responseIsReply = name === 'ask_user' && isAskUserReply(response);
                // Strip the framework's instructional wrapper from
                // ask_user results so the persisted display matches
                // the clean live-attached form ("Skapa modul" rather
                // than "The user selected: Skapa modul").
                if (response != null && name === 'ask_user') {
                    response = cleanAskUserResponse(response);
                }
                // #242 — for read tools, the paired tool-result message content
                // IS the JSON result, so hydrate `result` on resume; that lets a
                // reopened chat render the same rich card as the live turn.
                const result = RICH_CARD_TOOLS.has(name) && tc.id ? responses[tc.id] : undefined;
                out.push({ kind: 'tool', name, callId: tc.id, args, response, responseIsReply, result });
            }
        }
    }
    return out;
}

export default ChatThread;
